import { useNavigate } from 'react-router-dom';
import { ContentBox } from 'app/agriculture/components/ContentBox/ContentBox';
import { SimpleGrid } from 'app/agriculture/components/SimpleGrid/SimpleGrid';
import { Actions } from 'app/agriculture/components/Actions/Actions';
import { TaskIcon } from 'app/agriculture/icons';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTask } from 'app/agriculture/queries/tasks/useTask';
import { MapLocation } from 'app/agriculture/queries/types/MapLocation';
import { TextArea } from 'app/agriculture/forms/components/TextArea/TextArea';
import { EntityParentSelectField } from 'app/agriculture/forms/components/EntityParentSelectField/EntityParentSelectField';
import { SpecificLocationField } from 'app/agriculture/forms/components/SpecificLocationField/SpecificLocationField';
import { FormDiscardChangesBlocker } from 'app/agriculture/forms/components/FormDiscardChangesBlocker/FormDiscardChangesBlocker';
import { useEntityUpdateMutation } from 'app/agriculture/mutations/entity/update/useEntityUpdateMutation';
import { useRouteActions } from 'app/agriculture/router/useRouteActions';
import { TaskPriority } from 'app/agriculture/queries/tasks/types/TaskPriority';
import { TaskCategoryDropdown } from 'app/agriculture/routes/Tasks/TaskCreate/components/TaskCategoryDropdown';
import { Input } from 'app/agriculture/forms/components/Input/Input';
import { UserSelectField } from 'app/agriculture/forms/components/UserSelectField/UserSelectField';
import { TaskPriorityDropdown } from 'app/agriculture/routes/Tasks/TaskCreate/components/TaskPriorityDropdown';
import { CheckboxFieldComponent } from 'app/agriculture/forms/components/checkbox_field_component';
import './TaskEditForm.scss';
import { ShowOnMapButton } from 'app/agriculture/components/ContentBox/components/ShowOnMapButton';
import { useAttachOptions } from 'app/agriculture/hooks/useAttachOptions/useAttachOptions';
import { taskAttachOptionTypes } from 'app/agriculture/routes/Tasks/utils/taskAttachOptionTypes';
import { DatePickerInput } from 'app/agriculture/forms/components/DatePicker/DatePickerInput';
import { convertMapLocationToPosition } from 'app/agriculture/components/utils/convertMapLocationToPosition';
import { endOfDay, isAfter, isDate, startOfDay } from 'date-fns';
import { tz } from '@date-fns/tz';
import { useTimeZone } from 'app/agriculture/hooks/useTimeZone/useTimeZone';

interface TaskEditFields {
    title: string;
    category: string;
    description: string | null;
    location: MapLocation | null;
    priority: TaskPriority;

    startDate?: Date;
    endDate?: Date;

    parentIds: string[];
    assignees: number[];

    isComplete: boolean;
}

export function TaskEditForm({ taskId }: { taskId: string }) {
    const task = useTask({ taskId });
    const { timeZone } = useTimeZone();
    const navigate = useNavigate();
    const routeActions = useRouteActions();

    const form = useForm<TaskEditFields>({
        mode: 'all',
        defaultValues: {
            ...task,
        },
        shouldFocusError: true,
    });

    const {
        register,
        reset,
        setValue,
        handleSubmit,
        formState: { errors, isDirty },
    } = form;

    const { mutate, isPending } = useEntityUpdateMutation('Task');

    const onSubmit: SubmitHandler<TaskEditFields> = (formData) => {
        mutate(
            { id: taskId, ...formData },
            {
                onSuccess: () => {
                    reset({}, { keepValues: true });
                    navigate('../');
                },
                onError: () => {
                    reset({}, { keepValues: true });
                },
            },
        );
    };

    const attachOptions = useAttachOptions(taskAttachOptionTypes);

    return (
        <ContentBox
            title="Edit Task"
            Icon={TaskIcon}
            className="TaskEditForm"
            variant="md"
            showOnMapButton={
                <ShowOnMapButton position={convertMapLocationToPosition(task.location)} />
            }
        >
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <FormDiscardChangesBlocker />
                    <ContentBox.Section scrollable className="TaskEditForm__fields">
                        <TaskCategoryDropdown />
                        <Input
                            label="Title"
                            className="TaskCreate__title-input"
                            {...register('title', { required: 'Please add a title' })}
                            errorMessage={errors.title?.message}
                        />
                        <TextArea
                            label="Description"
                            {...register('description')}
                            className="TaskCreate__description-input"
                            errorMessage={errors.description?.message}
                        />
                        <EntityParentSelectField
                            label="Attach Task To"
                            fieldName="parentIds"
                            attachOptions={attachOptions}
                        />
                        <SpecificLocationField
                            isPin
                            entityType="Task"
                            initialValue={task.location}
                            onChange={(value) => {
                                setValue('location', value, { shouldDirty: true });
                            }}
                        />
                        <UserSelectField fieldName="assignees" label="Assignees" />
                        <SimpleGrid columns={2}>
                            <CheckboxFieldComponent
                                fieldName="isComplete"
                                formController={form}
                                noun="Completed"
                            />
                            <TaskPriorityDropdown />
                        </SimpleGrid>
                        <SimpleGrid columns={2}>
                            <Controller
                                control={form.control}
                                name="startDate"
                                rules={{
                                    validate: {
                                        invalidDateRange: (value, fields) => {
                                            if (value == null || fields.endDate == null) return;
                                            if (isAfter(value, fields.endDate)) {
                                                return 'Start date must be before end date.';
                                            }
                                        },
                                        invalidDate: (value) => {
                                            if (value == null || !isDate(value)) {
                                                return 'Please enter a valid date';
                                            }
                                        },
                                    },
                                }}
                                render={({ field, fieldState, formState }) => (
                                    <DatePickerInput
                                        id="entity-start-date-input"
                                        label="Start date"
                                        initialValue={formState.defaultValues?.startDate}
                                        errorMessage={fieldState.error?.message}
                                        onChange={(date?: Date) => {
                                            if (date == null) {
                                                field.onChange();
                                                return;
                                            }

                                            field.onChange(startOfDay(date, { in: tz(timeZone) }));
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                control={form.control}
                                name="endDate"
                                rules={{
                                    validate: {
                                        invalidDate: (value) => {
                                            if (value == null || !isDate(value)) {
                                                return 'Please enter a valid date';
                                            }
                                        },
                                    },
                                }}
                                render={({ field, fieldState, formState }) => (
                                    <DatePickerInput
                                        id="entity-end-date-input"
                                        label="End date"
                                        initialValue={formState.defaultValues?.endDate}
                                        errorMessage={fieldState.error?.message}
                                        onChange={(date?: Date) => {
                                            if (date == null) {
                                                field.onChange();
                                                return;
                                            }

                                            field.onChange(endOfDay(date, { in: tz(timeZone) }));
                                            void form.trigger('startDate');
                                        }}
                                    />
                                )}
                            />
                        </SimpleGrid>
                        <Actions
                            sticky
                            primaryAction={{
                                text: 'Save task changes',
                                type: 'submit',
                                isLoading: isPending,
                                disabled: !isDirty,
                            }}
                            secondaryActions={[
                                {
                                    startIcon: 'delete',
                                    text: 'Delete',
                                    theme: 'danger',
                                    onClick: () => {
                                        routeActions.remove(taskId, { type: 'Task' });
                                    },
                                },
                            ]}
                            backAction={{
                                text: 'Back',
                                onClick: () => {
                                    navigate(-1);
                                },
                            }}
                        />
                    </ContentBox.Section>
                </form>
            </FormProvider>
        </ContentBox>
    );
}
